<template>
  <div :class="bem.b('json-xml')">
    <div :class="bem.e('header')">
      <ll-segmented
        v-model:value="type"
        size="middle"
        :options="jsonXmlOptions"
        @change="handleTypeChange"
      >
        <template #label="{ payload }">
          <div>{{ payload.title }}</div>
        </template>
      </ll-segmented>
      <div class="flex">
        <template v-if="type === 'json'">
          <ll-button type="link" :disabled="disabled">
            <!-- <ll-icon/> -->
            预览
          </ll-button>
          <ll-button type="link" :disabled="disabled">
            <!-- <ll-icon/> -->
            生成代码
          </ll-button>
          <ll-button type="link" :disabled="disabled">
            <!-- <ll-icon/> -->
            JSON Schema
          </ll-button>
        </template>
        <template v-else>
          <ll-button type="link" :disabled="disabled">
            <!-- <ll-icon/> -->
            格式化
          </ll-button>
          <ll-button type="link" :disabled="disabled">
            <!-- <ll-icon/> -->
            生成代码
          </ll-button>
          <ll-dropdown
            :disabled="disabled"
            :dropdown-items="customOverlay"
            placement="bottomRight"
          >
            <ll-icon icon-name="icon-line-arrow-down" />
          </ll-dropdown>
        </template>
      </div>
    </div>
    <div :class="bem.e('body')">
      <template v-if="type === 'json'">
        <ll-code-editor
          v-model:value="innerValue"
          :disabled="disabled"
          style="height: 200px"
          :options="{
            minimap: {
              enabled: false
            }
          }"
          language="json"
        />
      </template>
      <template v-else>
        <tree-table
          :type="props.type"
          :data-source="dataSource"
          @change="handleChange"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, inject } from 'vue'

import { isEmpty } from 'lodash-es'

import { createNamespace } from '@ll-plus/utils'

import {
  DEFAULT_TYPE,
  jsonXmlOptions,
  customOverlay,
  jsonXmlProps,
  jsonXmlEmits,
  type IJsonXmlItem
} from '../config'

import TreeTable from './json-xml-table.vue'

defineOptions({ name: 'LlJsonXml' })

const props = defineProps(jsonXmlProps)

const emits = defineEmits(jsonXmlEmits)

const bem = createNamespace('json-xml')

const disabled = inject('disabled') as boolean

const type = ref(DEFAULT_TYPE)

const innerValue = ref<any>(JSON.stringify(props.value))

const dataSource = ref([
  {
    key: '1',
    name: '根节点',
    type: 'object',
    value: '',
    chineseName: '',
    description: '',
    mock: '',
    children: []
  }
] as IJsonXmlItem[])

const parseJson2Array = (
  json: object,
  parentKey: string = '1',
  parentType: string = 'object'
) => {
  let num = 0
  const result = [] as IJsonXmlItem[]
  for (const key in json) {
    num++
    if (Object.prototype.hasOwnProperty.call(json, key)) {
      const element = json[key]
      if (typeof element === 'object' || Array.isArray(element)) {
        const currentType = Array.isArray(element) ? 'array' : 'object'
        result.push({
          key: `${parentKey}-${num}`,
          name: parentType === 'array' ? 'ITEM' : key,
          type: currentType,
          value:
            parentType === 'array' && currentType !== 'object' ? element : '',
          chineseName: '',
          description: '',
          mock: '',
          children: parseJson2Array(element, `${parentKey}-${num}`, currentType)
        } as IJsonXmlItem)
      } else {
        result.push({
          key: `${parentKey}-${num}`,
          name: parentType === 'array' ? 'ITEM' : key,
          type: typeof element,
          value: '',
          chineseName: '',
          description: '',
          mock: element
        } as IJsonXmlItem)
      }
    }
  }
  return result
}

const parseArray2Json = (data: IJsonXmlItem[]) => {
  const result = {} as any
  data.forEach((item: IJsonXmlItem) => {
    if (item.name) {
      if (item.type === 'object' || item.type === 'array') {
        if (item.children && item.children.length) {
          result[item.name] = parseArray2Json(item.children)
        }
      } else {
        result[item.name] = item.mock
      }
    }
  })
  return result
}

const handleTypeChange = e => {
  if (e === 'json') {
    innerValue.value = JSON.stringify(parseArray2Json(dataSource.value))
  } else {
    try {
      const value = JSON.parse(innerValue.value)
      if (typeof value === 'object') {
        dataSource.value = parseJson2Array(value)
      } else {
        dataSource.value = [
          {
            key: '1',
            name: '根节点',
            type: typeof value,
            value: '',
            chineseName: '',
            description: '',
            mock: value,
            children: []
          }
        ] as IJsonXmlItem[]
      }
    } catch (error) {
      dataSource.value = [
        {
          key: '1',
          name: '根节点',
          type: typeof innerValue.value,
          value: '',
          chineseName: '',
          description: '',
          mock: innerValue.value,
          children: []
        }
      ] as IJsonXmlItem[]
    }
  }
}

const handleChange = arr => {
  dataSource.value = arr
  emits('update:value', parseArray2Json(arr))
}

watch(
  () => props.value,
  n => {
    if (!isEmpty(n)) {
      if (typeof n === 'object') {
        innerValue.value = JSON.stringify(n)
        dataSource.value = parseJson2Array(props.value)
      } else {
        innerValue.value = n
        dataSource.value = [
          {
            key: '1',
            name: '根节点',
            type: typeof props.value,
            value: '',
            chineseName: '',
            description: '',
            mock: n,
            children: []
          }
        ] as IJsonXmlItem[]
      }
    } else {
      innerValue.value = ''
      dataSource.value = [
        {
          key: '1',
          name: '根节点',
          type: 'object',
          value: '',
          chineseName: '',
          description: '',
          mock: '',
          children: []
        }
      ] as IJsonXmlItem[]
    }
  },
  {
    deep: true,
    immediate: true
  }
)

watch(innerValue, n => {
  try {
    const value = JSON.parse(n)
    if (typeof value === 'object') {
      emits('update:value', value)
    } else {
      emits('update:value', value)
    }
  } catch (error) {
    emits('update:value', n)
  }
})
</script>
